import cookie from 'cookie';

type CookieChangeHandler = (value: string) => void;
export type CookieAdapter = (cookieToSet: string) => void;

/**
 * In-memory cookie store. Allows subscription to individual cookie value
 * changes and handles writes via a passed-in adapter function. Intended for use
 * as the single unchanging context value for a CookieProvider component.
 */
export default class CookieManager {
    private data: {[name: string]: string};
    private handlers: {[name: string]: CookieChangeHandler[] | void};
    private adapter: CookieAdapter;

    /**
     * @private Default adapter, writes directly to document.cookie
     */
    private static documentCookieAdapter = (cookieToSet: string) =>
        document &&
        typeof document.cookie === 'string' &&
        (document.cookie = cookieToSet);

    /**
     * Create a CookieManager instance.
     * @param {string} cookieStr - initial cookie string. pass in either document.cookie or a server-side request cookie string
     * @param {function} consentAdapter - called during write operations to allow for GDPR consent handling. Defaults to documentCookieAdapter.
     */
    constructor(cookieStr = '', consentAdapter?: CookieAdapter) {
        this.data = cookie.parse(cookieStr, {decode: (s) => s});
        this.handlers = {};
        this.adapter = consentAdapter || CookieManager.documentCookieAdapter;
    }

    /**
     * Get the current un-decoded in-memory value of a single cookie.
     * @param {string} name - The cookie name
     * @returns {string} The cookie's decoded string value
     */
    get(name: string): string | void {
        return this.data[name];
    }

    /**
     * Set a cookie value.
     * @param {string} name - The cookie name
     * @param {unknown} value - The raw value to write
     * @param {CookieSerializeOptions} options - Serialization options object passed to `cookie.serialize`
     */
    set(name: string, value: unknown, options?: cookie.CookieSerializeOptions) {
        if (typeof value === 'object') {
            throw new Error(
                "Can't set a cookie to a complex value. Consider using JSON.stringify to serialize your value first."
            );
        }
        const encoded = (options?.encode ?? encodeURIComponent)(String(value));
        this.data[name] = encoded;
        this.adapter(
            cookie.serialize(name, encoded, {...options, encode: (s) => s})
        );
        for (const handler of this.handlers[name] || []) {
            handler(encoded);
        }
    }

    /**
     * Subscribe to cookie change events.
     * @param {string} name - The cookie to subscribe to
     * @param {CookieChangeHandler} handler - Function to be called when cookie value is set
     * @returns {function} - An unsubscribe function
     */
    subscribe(name: string, handler: CookieChangeHandler) {
        // typescript can't narrow an indexed object property so assign to a variable
        const handlers = this.handlers[name];
        if (!Array.isArray(handlers)) {
            this.handlers[name] = [handler];
        } else {
            handlers.push(handler);
        }

        return () => {
            this.handlers[name] = (handlers || []).filter((h) => h !== handler);
        };
    }
}
